import { Alert, Card, Descriptions, Input, Space, Spin, Table, Tag, Typography } from 'antd';
import { useFetchApiGet } from 'hooks/api/useFetchApi';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import nhsvAccountDetailApiCall from 'apps/nhsv/apiCalls/nhsv/account-detail';

const { Title } = Typography;

const formatNumber = (value: string | undefined) => {
  if (!value) return '-';
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return num.toLocaleString('vi-VN');
};

const stockColumns = [
  { title: 'Stock Code', dataIndex: 'stk_cd', key: 'stk_cd', width: 100 },
  { title: 'Total Qty', dataIndex: 'own_qty', key: 'own_qty', render: formatNumber, align: 'right' as const },
  { title: 'Sellable Qty', dataIndex: 'sell_able_qty', key: 'sell_able_qty', render: formatNumber, align: 'right' as const },
  { title: 'Buy Price', dataIndex: 'buy_uv', key: 'buy_uv', render: formatNumber, align: 'right' as const },
  { title: 'Current Price', dataIndex: 'cur_pri', key: 'cur_pri', render: formatNumber, align: 'right' as const },
  { title: 'Book Value', dataIndex: 'book_amt', key: 'book_amt', render: formatNumber, align: 'right' as const },
  { title: 'Market Value', dataIndex: 'eval_amt', key: 'eval_amt', render: formatNumber, align: 'right' as const },
  {
    title: 'P&L',
    dataIndex: 'pnl_amount',
    key: 'pnl_amount',
    render: (value: string) => {
      const num = parseFloat(value);
      if (isNaN(num)) return '-';
      const color = num > 0 ? '#3f8600' : num < 0 ? '#cf1322' : undefined;
      return <span style={{ color }}>{formatNumber(value)}</span>;
    },
    align: 'right' as const,
  },
  {
    title: 'P&L %',
    dataIndex: 'eval_per',
    key: 'eval_per',
    render: (value: string) => {
      const num = parseFloat(value);
      if (isNaN(num)) return '-';
      const color = num > 0 ? '#3f8600' : num < 0 ? '#cf1322' : undefined;
      return <span style={{ color }}>{num.toFixed(2)}%</span>;
    },
    align: 'right' as const,
  },
  { title: 'T0 Buy', dataIndex: 'td_buy_mth_qty', key: 'td_buy_mth_qty', render: formatNumber, align: 'right' as const },
  { title: 'T0 Sell', dataIndex: 'td_sell_mth_qty', key: 'td_sell_mth_qty', render: formatNumber, align: 'right' as const },
];

export default function NHSVAccountDetailPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlAccountNumber = searchParams.get('account_number') || '';
  const [inputValue, setInputValue] = useState(urlAccountNumber);
  const { data, load, isLoading } = useFetchApiGet(nhsvAccountDetailApiCall, {
    resourceName: 'data',
  });

  useEffect(() => {
    if (urlAccountNumber) {
      load({ account_number: urlAccountNumber });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setSearchParams({ account_number: trimmed });
    setInputValue(trimmed);
    load({ account_number: trimmed });
  };

  const accountInfo = data?.account_info;
  const totalAsset = data?.total_asset;
  const stockBalance = data?.stock_balance || [];
  const cashBalance = data?.cash_balance;
  const vsdStatus = data?.vsd_status;
  const contractSigned = data?.contract_signed;
  const canSync = data?.can_sync !== false;

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>NHSV Account Detail</Title>

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Input.Search
          placeholder="Enter trading account number"
          enterButton="Lookup"
          size="large"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onSearch={handleSearch}
          style={{ maxWidth: 500 }}
          allowClear
        />

        {isLoading && (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        )}

        {data && !isLoading && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Card title={`Account Info — ${searchParams.get('account_number') || ''}`} size="small">
              <Descriptions bordered column={{ xs: 1, sm: 2, lg: 3 }} size="small">
                <Descriptions.Item label="Account Number (NHSV)">
                  {accountInfo?.acnt_no || searchParams.get('account_number') || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Contract Number (NHSV)">
                  {accountInfo?.cntr_no || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="VSD Status">
                  {(() => {
                    const map: Record<string, { color: string; label: string }> = {
                      '1': { color: 'processing', label: 'Pending' },
                      '2': { color: 'success', label: 'Approved' },
                      '3': { color: 'error', label: 'Rejected' },
                    };
                    const info = map[vsdStatus];
                    if (!info) return vsdStatus || '-';
                    return <Tag color={info.color}>{info.label}</Tag>;
                  })()}
                </Descriptions.Item>
                <Descriptions.Item label="Contract Signed">
                  <Tag color={contractSigned ? 'success' : 'default'}>
                    {contractSigned ? 'Yes' : 'No'}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {canSync ? (
              <>
                <Card title="Asset Summary" size="small">
                  <Descriptions bordered column={{ xs: 1, sm: 2, lg: 3 }} size="small">
                    <Descriptions.Item label="Total Asset">{formatNumber(totalAsset?.total_asset)}</Descriptions.Item>
                    <Descriptions.Item label="Net Asset">{formatNumber(totalAsset?.not_total_asset)}</Descriptions.Item>
                    <Descriptions.Item label="Cash Total">{formatNumber(totalAsset?.cash_total)}</Descriptions.Item>
                    <Descriptions.Item label="Stock Value">{formatNumber(totalAsset?.stk_amt_total)}</Descriptions.Item>
                    <Descriptions.Item label="Available Stock Value">{formatNumber(totalAsset?.able_qty_value)}</Descriptions.Item>
                    <Descriptions.Item label="Blocked Stock Value">{formatNumber(totalAsset?.lim_qty_value)}</Descriptions.Item>
                    <Descriptions.Item label="Buy Waiting">{formatNumber(totalAsset?.buy_wait_amt)}</Descriptions.Item>
                    <Descriptions.Item label="Cash (Not Hold)">{formatNumber(totalAsset?.cash_not_hold)}</Descriptions.Item>
                    <Descriptions.Item label="Cash (Hold)">{formatNumber(totalAsset?.cash_hold)}</Descriptions.Item>
                    <Descriptions.Item label="Total Loan">{formatNumber(totalAsset?.loan_total)}</Descriptions.Item>
                    <Descriptions.Item label="Loan Interest">{formatNumber(totalAsset?.loan_int)}</Descriptions.Item>
                    <Descriptions.Item label="Unsettled Buy">{formatNumber(totalAsset?.all_prf_net)}</Descriptions.Item>
                    <Descriptions.Item label="Pending Buy">{formatNumber(totalAsset?.not_yet_math_amout)}</Descriptions.Item>
                    <Descriptions.Item label="Cash Advance">{formatNumber(totalAsset?.notyet_pia_loan_amt)}</Descriptions.Item>
                    <Descriptions.Item label="Dividend Cash">{formatNumber(totalAsset?.rgt_cash)}</Descriptions.Item>
                    <Descriptions.Item label="Rights Stock Value">{formatNumber(totalAsset?.rgt_dpo)}</Descriptions.Item>
                    <Descriptions.Item label="Awaiting Trade Stock Value">{formatNumber(totalAsset?.delay_rate)}</Descriptions.Item>
                  </Descriptions>
                </Card>

                <Card title="Stock Positions" size="small">
                  <Table
                    dataSource={stockBalance}
                    columns={stockColumns}
                    rowKey="stk_cd"
                    pagination={false}
                    size="small"
                    scroll={{ x: 1200 }}
                    locale={{ emptyText: 'No stock positions' }}
                  />
                </Card>

                <Card title="Cash Balance" size="small">
                  <Descriptions bordered column={{ xs: 1, sm: 2, lg: 3 }} size="small">
                    <Descriptions.Item label="Withdrawable Cash">{formatNumber(cashBalance?.tot_out_psbamt)}</Descriptions.Item>
                    <Descriptions.Item label="Total Cash (Dpo)">{formatNumber(cashBalance?.dpo)}</Descriptions.Item>
                    <Descriptions.Item label="Matched Amount">{formatNumber(cashBalance?.mth_amt)}</Descriptions.Item>
                    <Descriptions.Item label="Blocked Deposit">{formatNumber(cashBalance?.dpo_block)}</Descriptions.Item>
                    <Descriptions.Item label="Outstanding Deposit">{formatNumber(cashBalance?.outq_dpo_bk)}</Descriptions.Item>
                    <Descriptions.Item label="Unpaid Loan">{formatNumber(cashBalance?.nonrpy_loan_amt)}</Descriptions.Item>
                    <Descriptions.Item label="All P&L">{formatNumber(cashBalance?.all_prf)}</Descriptions.Item>
                    <Descriptions.Item label="Credit Deposit">{formatNumber(cashBalance?.crd_dpo)}</Descriptions.Item>
                    <Descriptions.Item label="Substitute Deposit">{formatNumber(cashBalance?.sbst_dpo)}</Descriptions.Item>
                  </Descriptions>
                </Card>
              </>
            ) : (
              <Alert
                type="info"
                showIcon
                message="Asset data unavailable"
                description="This account has not enabled NHSV data sync, so asset, stock and cash balances are not fetched."
              />
            )}
          </Space>
        )}
      </Space>
    </div>
  );
}
